import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./App.css";

// ─── Components ────────────────────────────────────
import { SEOHead, LOCAL_BUSINESS_SCHEMA } from "./components/SEOHead";
import { Header, Footer, ChatWidget, CouponPopup, ExitIntentPopup } from "./components/Layout";

// ─── Pages ─────────────────────────────────────────
import HomePage from "./pages/HomePage";
import TextUsWidget from "./components/TextUsWidget";
import ContentPage from "./pages/ContentPage";
import BlogListPage from "./pages/BlogListPage";
import BlogPostPage, { BlogRedirect } from "./pages/BlogPostPage";

// Legacy PHP URL redirect handler
function LegacyPhpRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const urlParam = params.get("url");
  const target = urlParam ? `/${urlParam.replace(/_/g, "-")}` : "/";
  return <Navigate to={target} replace />;
}
import SMSPolicyPage from "./pages/SMSPolicyPage";
import TrainingPage from "./pages/TrainingPage";
import GeoListPage from "./pages/GeoListPage";
import GeoLandingPage from "./pages/GeoLandingPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import FAQPage from "./pages/FAQPage";
import AdminPage from "./pages/AdminPage";

// Redirect www to non-www for SEO (canonical URL enforcement)
if (typeof window !== 'undefined' && window.location.hostname.startsWith('www.')) {
  window.location.href = window.location.href.replace('www.', '');
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Legacy URL Redirect Handler ───────────────────
const LEGACY_REDIRECTS = { "/Cleaning-Process-DFW-Homes-Houses.html": "/Cleaning-Process", "/Cleaning-Process-DFW-Homes-Houses": "/Cleaning-Process", "/privacy/privacy.htm": "/sms-policy", "/privacy/privacy": "/sms-policy", "/Costs/house-cleaning-price-quote.html": "/Costs", "/Costs/house-cleaning-price-quote": "/Costs" };

function LegacyRedirectHandler() {
  const location = useLocation();
  const path = location.pathname;
  if (LEGACY_REDIRECTS[path]) return <Navigate to={LEGACY_REDIRECTS[path]} replace />;
  if (path.endsWith(".html")) return <Navigate to={path.slice(0, -5)} replace />;
  if (path.endsWith(".htm")) return <Navigate to={path.slice(0, -4)} replace />;
  return null;
}

// ─── Structured Data ───────────────────────────────
function StructuredData() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LOCAL_BUSINESS_SCHEMA) }} />;
}

// ─── App Content (Router) ──────────────────────────
function AppContent({ settings, homepage, navPages, banners, videos, featuredPages, reviews, reviewLink }) {
  const [showCoupon, setShowCoupon] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [exitIntentShown, setExitIntentShown] = useState(false);

  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitIntentShown && !showCoupon && !showExitIntent) {
        const seen = sessionStorage.getItem('exitIntentShown');
        if (!seen) { setShowExitIntent(true); setExitIntentShown(true); sessionStorage.setItem('exitIntentShown', 'true'); }
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [exitIntentShown, showCoupon, showExitIntent]);

  const handleCouponClick = () => { setShowExitIntent(false); setShowCoupon(true); setExitIntentShown(true); sessionStorage.setItem('exitIntentShown', 'true'); };

  return (
    <div className="App" data-testid="app-root">
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css" />
      <Header settings={settings} navPages={navPages} />
      <LegacyRedirectHandler />
      <button className="fifty-off-tab" onClick={handleCouponClick} data-testid="fifty-off-tab">$50 OFF</button>
      {showCoupon && <CouponPopup onClose={() => setShowCoupon(false)} />}
      {showExitIntent && <ExitIntentPopup onClose={() => setShowExitIntent(false)} />}
      <Routes>
        {/* Legacy redirects - old .html URLs */}
        <Route path="/Cleaning-Process-DFW-Homes-Houses.html" element={<Navigate to="/Cleaning-Process" replace />} />
        <Route path="/Cleaning-Process-DFW-Homes-Houses" element={<Navigate to="/Cleaning-Process" replace />} />
        <Route path="/privacy/privacy.htm" element={<Navigate to="/sms-policy" replace />} />
        <Route path="/privacy/privacy" element={<Navigate to="/sms-policy" replace />} />
        <Route path="/Costs/house-cleaning-price-quote.html" element={<Navigate to="/Costs" replace />} />
        <Route path="/Costs/house-cleaning-price-quote" element={<Navigate to="/Costs" replace />} />
        <Route path="/Costs/house-cleaning-service-quote.html" element={<Navigate to="/Costs" replace />} />
        <Route path="/Costs/house-cleaning-service-quote" element={<Navigate to="/Costs" replace />} />
        <Route path="/housekeeping-service-areas.html" element={<Navigate to="/service-areas" replace />} />
        <Route path="/housekeeping-service-areas" element={<Navigate to="/service-areas" replace />} />
        <Route path="/housekeeping-service-areas/arlington-service-area" element={<Navigate to="/service-areas/house-cleaning-arlington-tx" replace />} />
        <Route path="/housekeeping-service-areas/arlington-service-area.html" element={<Navigate to="/service-areas/house-cleaning-arlington-tx" replace />} />
        <Route path="/housekeeping-service-areas/fort-worth-area" element={<Navigate to="/service-areas/house-cleaning-fort-worth-tx" replace />} />
        <Route path="/housekeeping-service-areas/fort-worth-area.html" element={<Navigate to="/service-areas/house-cleaning-fort-worth-tx" replace />} />
        <Route path="/housekeeping-service-areas/grand-prairie-area" element={<Navigate to="/service-areas/house-cleaning-grand-prairie-tx" replace />} />
        <Route path="/housekeeping-service-areas/grand-prairie-area.html" element={<Navigate to="/service-areas/house-cleaning-grand-prairie-tx" replace />} />
        <Route path="/housekeeping-service-areas/kennedale-area" element={<Navigate to="/service-areas/house-cleaning-kennedale-tx" replace />} />
        <Route path="/housekeeping-service-areas/kennedale-area.html" element={<Navigate to="/service-areas/house-cleaning-kennedale-tx" replace />} />
        <Route path="/housekeeping-service-areas/mansfield-area" element={<Navigate to="/service-areas/house-cleaning-mansfield-tx" replace />} />
        <Route path="/housekeeping-service-areas/mansfield-area.html" element={<Navigate to="/service-areas/house-cleaning-mansfield-tx" replace />} />
        <Route path="/housekeeping-service-areas/kennedale-texas-housekeeping-service-area.html" element={<Navigate to="/service-areas/house-cleaning-kennedale-tx" replace />} />
        <Route path="/housekeeping-service-areas/kennedale-texas-housekeeping-service-area" element={<Navigate to="/service-areas/house-cleaning-kennedale-tx" replace />} />
        <Route path="/Costs.html" element={<Navigate to="/Costs" replace />} />
        <Route path="/Fun.html" element={<Navigate to="/Fun" replace />} />
        <Route path="/Contact-Us.html" element={<Navigate to="/Contact-Us" replace />} />
        <Route path="/Employment.html" element={<Navigate to="/Employment" replace />} />
        <Route path="/employment.html" element={<Navigate to="/Employment" replace />} />
        <Route path="/Why-Choose-Lazy-Days-Home-Services.html" element={<Navigate to="/Why-Choose-Lazy-Days-Home-Services" replace />} />
        <Route path="/Gift-Certificate.html" element={<Navigate to="/Gift-Certificate" replace />} />
        <Route path="/Cleaning-Process.html" element={<Navigate to="/Cleaning-Process" replace />} />
        {/* Old PHP/legacy URLs */}
        <Route path="/sms-textpolicy.html" element={<Navigate to="/sms-policy" replace />} />
        <Route path="/sms-textpolicy" element={<Navigate to="/sms-policy" replace />} />
        <Route path="/coupon.php" element={<Navigate to="/" replace />} />
        <Route path="/401.html" element={<Navigate to="/" replace />} />
        <Route path="/page.php" element={<LegacyPhpRedirect />} />
        {/* Main routes */}
        <Route path="/" element={<HomePage homepage={homepage} settings={settings} banners={banners} videos={videos} featuredPages={featuredPages} reviews={reviews} reviewLink={reviewLink} />} />
        <Route path="/cleaning-news" element={<BlogListPage navPages={navPages} />} />
        <Route path="/cleaning-news/:slug" element={<BlogPostPage navPages={navPages} />} />
        <Route path="/blog" element={<Navigate to="/cleaning-news" replace />} />
        <Route path="/blog/:slug" element={<BlogRedirect />} />
        <Route path="/service-areas" element={<GeoListPage navPages={navPages} />} />
        <Route path="/service-areas/:slug" element={<GeoLandingPage navPages={navPages} />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage navPages={navPages} />} />
        <Route path="/faq" element={<FAQPage navPages={navPages} />} />
        <Route path="/training" element={<TrainingPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/sms-policy" element={<SMSPolicyPage navPages={navPages} />} />
        <Route path="/:pageUrl" element={<ContentPage navPages={navPages} />} />
        <Route path="/:pageUrl/:subUrl" element={<ContentPage navPages={navPages} />} />
      </Routes>
      <Footer navPages={navPages} />
      <ChatWidget reviewLink={reviewLink} />
      <TextUsWidget />
      <a href="https://web-techservices.com" target="_blank" rel="noreferrer" className="custom-badge" data-testid="custom-badge">Web-Techservices AI</a>
    </div>
  );
}

// ─── App (Root) ────────────────────────────────────
function App() {
  const [settings, setSettings] = useState({ phone: "(817) 277-7387", facebook: "", google: "", email: "" });
  const [homepage, setHomepage] = useState(null); const [navPages, setNavPages] = useState([]); const [banners, setBanners] = useState([]);
  const [videos, setVideos] = useState([]); const [featuredPages, setFeaturedPages] = useState([]); const [reviews, setReviews] = useState([]); const [reviewLink, setReviewLink] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (retryCount = 0) => {
    try {
      const [s, h, n, b, v, f, r, rl] = await Promise.all([
        axios.get(`${API}/settings`).catch(() => ({ data: settings })),
        axios.get(`${API}/homepage`).catch(() => ({ data: null })),
        axios.get(`${API}/pages/nav`).catch(() => ({ data: [] })),
        axios.get(`${API}/banners`).catch(() => ({ data: [] })),
        axios.get(`${API}/homevideos`).catch(() => ({ data: [] })),
        axios.get(`${API}/pages/featured`).catch(() => ({ data: [] })),
        axios.get(`${API}/reviews`).catch(() => ({ data: [] })),
        axios.get(`${API}/reviews/link`).catch(() => ({ data: { link: "" } })),
      ]);
      setSettings(s.data); setHomepage(h.data); setNavPages(n.data); setBanners(b.data); setVideos(v.data); setFeaturedPages(f.data);
      const reviewData = r.data;
      if (reviewData.reviews) { setReviews(reviewData.reviews); setReviewLink(reviewData.review_link || rl.data.link || ""); }
      else { setReviews(Array.isArray(reviewData) ? reviewData : []); setReviewLink(rl.data.link || ""); }
      window.__gbpRating = reviewData.rating || 0;
      window.__gbpTotal = reviewData.total || 0;
      // Retry if nav failed to load (server may have been restarting)
      if (n.data.length === 0 && retryCount < 3) {
        setTimeout(() => fetchData(retryCount + 1), 2000);
      }
    } catch (err) {
      console.error(err);
      if (retryCount < 3) { setTimeout(() => fetchData(retryCount + 1), 2000); }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="loading-spinner" style={{ minHeight: "100vh" }}></div>;
  return (<BrowserRouter><StructuredData /><AppContent settings={settings} homepage={homepage} navPages={navPages} banners={banners} videos={videos} featuredPages={featuredPages} reviews={reviews} reviewLink={reviewLink} /></BrowserRouter>);
}

export default App;
